import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type {
  CfoContractMeta,
  QuickBooksFpaForecastGrain,
  QuickBooksFpaForecastValueCase,
  QuickBooksFpaMlCashDailyForecast,
  QuickBooksFpaMlForecastPackageSnapshotResponse,
  QuickBooksFpaMlStatementForecastResponse,
  QuickBooksFpaStatementRender,
  QuickBooksFpaStatementRow,
  ServerApiClient,
} from '../../../data/ServerApiClient'
import { materializeQuickBooksGlDetailTable, QBO_GL_DETAIL_TABLE, sqlString } from '../quickBooksGlDetailShared'
import { trimStr } from './cfoFormat'
import { snapshotHasParquetArtifacts } from './forecastArtifactsDuckDb'
import {
  actualPeriodsForForecastGrain,
  addDaysIso,
  cashForecastHorizonEndIso,
  dateRangeOverlapDays,
  daysBetweenIso,
  daysInMonthIso,
  forecastStartIso,
  monthOffsetIso,
  monthStartIso,
  periodEndExclusiveIso,
  weekStartIso,
} from './forecastBuildState'
import {
  addVectorInto,
  addVectors,
  cloneRowWithValues,
  emptyValues,
  hasStatementValue,
  normalizeForecastRowLabel,
  scaleVector,
} from './forecastDriverEngine'

export type StatementKind = 'cash_flow' | 'profit_and_loss' | 'balance_sheet'

const cashDailyValueForCase = (row: QuickBooksFpaMlCashDailyForecast, valueCase: QuickBooksFpaForecastValueCase | string): number => {
  if (valueCase === 'high') return Number(row.high_amount ?? row.expected_amount ?? 0)
  if (valueCase === 'low') return Number(row.low_amount ?? row.expected_amount ?? 0)
  return Number(row.expected_amount ?? 0)
}

const isDailyCashSummaryRow = (row: QuickBooksFpaMlCashDailyForecast): boolean => {
  const text = `${row.cash_event_type || ''} ${row.source_type || ''}`.toLowerCase()
  return text.includes('net_operating_cash') || text.includes('summary')
}

const forecastPackageId = (snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse): string =>
  trimStr((snapshot.package as any)?.id || (snapshot.package as any)?.package_id || (snapshot as any).package_id)

const buildClientForecastContract = (
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  statementType: StatementKind | string,
  periods: string[],
  opts?: { readiness?: CfoContractMeta['readiness']; executionEngine?: CfoContractMeta['execution_engine']; warnings?: string[] },
): CfoContractMeta => {
  const packageId = forecastPackageId(snapshot)
  const asOf = trimStr(snapshot.as_of_date)
  const freshnessToken = [packageId, statementType, asOf, periods[0] || '', periods[periods.length - 1] || '']
    .filter(Boolean)
    .join(':') || 'client-forecast-snapshot'
  return {
    contract_id: 'quickbooks.cfo.forecast',
    contract_version: 'client-direct-cash-v1',
    generated_at: new Date().toISOString(),
    freshness_token: freshnessToken,
    readiness: opts?.readiness || 'ready',
    rebuild_state: opts?.readiness === 'rebuilding' ? 'warming' : 'ready',
    execution_engine: opts?.executionEngine || 'embedded_duckdb',
    sources: [
      {
        source_id: 'quickbooks_fpa_ml_forecast_package_snapshot',
        source_kind: 'forecast_package_snapshot',
        execution_engine: opts?.executionEngine || 'embedded_duckdb',
        artifact_ids: packageId ? [packageId] : [],
        row_count: null,
        freshness_token: packageId || null,
      },
    ],
    warnings: opts?.warnings || [],
    row_count: null,
  }
}

// TODO: the JSON-snapshot path (directCashBucketFromDaily + buildDirectCashForecastFromSnapshot)
// and the DuckDB SQL path (the CASE bucketing inside loadDirectCashForecastFromDuckDb) implement
// near-identical direct-cash bucketing/rollup logic twice. Collapse them into one shared rollup
// core once the JS-regex and SQL-LIKE matching semantics can be unified without changing output.
const directCashBucketFromDaily = (row: QuickBooksFpaMlCashDailyForecast, amount: number): string => {
  const text = [
    row.cash_event_type,
    row.source_type,
    row.source_id,
    row.counterparty_name,
    JSON.stringify(row.evidence_json || {}),
  ].map((value) => String(value || '').toLowerCase()).join(' ')
  if (/owner|member|partner|draw|distribution|contribution|equity/.test(text)) return 'fg-forecast-cash-owner-equity'
  if (amount >= 0) {
    return text.includes('customer') || text.includes('receipt') || text.includes('ar')
      ? 'fg-forecast-cash-customer-receipts'
      : 'fg-forecast-cash-other-receipts'
  }
  if (/payroll|wage|salary/.test(text)) return 'fg-forecast-cash-payroll'
  if (/tax|debt|loan|capex|capital/.test(text)) return 'fg-forecast-cash-debt-tax-capex'
  if (/vendor|disbursement|expense/.test(text)) return 'fg-forecast-cash-vendors'
  return 'fg-forecast-cash-other-disbursements'
}

const periodStartForCashRow = (date: string, grain: QuickBooksFpaForecastGrain | string): string =>
  grain === 'daily' ? date : grain === 'weekly' ? weekStartIso(date) : monthStartIso(date)

const directCashStatementRowsFromValues = (valuesByNode: Map<string, Array<number | null>>, periodCount: number): QuickBooksFpaStatementRow[] => {
  const rowDefs: Array<[string, string, string | null, number, string]> = [
    ['fg-forecast-cash-receipts', 'Receipts', null, 0, 'group'],
    ['fg-forecast-cash-customer-receipts', 'Customer receipts', 'fg-forecast-cash-receipts', 1, 'account'],
    ['fg-forecast-cash-other-receipts', 'Other receipts', 'fg-forecast-cash-receipts', 1, 'account'],
    ['fg-forecast-cash-disbursements', 'Disbursements', null, 0, 'group'],
    ['fg-forecast-cash-payroll', 'Payroll and people costs', 'fg-forecast-cash-disbursements', 1, 'account'],
    ['fg-forecast-cash-vendors', 'Vendors and operating expenses', 'fg-forecast-cash-disbursements', 1, 'account'],
    ['fg-forecast-cash-debt-tax-capex', 'Debt, tax, and capital items', 'fg-forecast-cash-disbursements', 1, 'account'],
    ['fg-forecast-cash-other-disbursements', 'Other disbursements', 'fg-forecast-cash-disbursements', 1, 'account'],
    ['fg-forecast-cash-equity', 'Changes in equity', null, 0, 'group'],
    ['fg-forecast-cash-owner-equity', 'Owner draws and contributions', 'fg-forecast-cash-equity', 1, 'account'],
    ['fg-forecast-cash-net-change', 'Net cash change', null, 0, 'formula'],
    ['fg-forecast-cash-beginning-cash', 'Beginning cash', null, 0, 'formula'],
    ['fg-forecast-cash-ending-cash', 'Ending cash', null, 0, 'formula'],
  ]
  return rowDefs.map(([nodeId, label, parentNodeId, level, rowType]) => ({
    node_id: nodeId,
    parent_node_id: parentNodeId,
    row_type: rowType,
    statement_type: 'cash_flow',
    label,
    level,
    account_id: null,
    account_number: null,
    cash_flow_section: 'direct_cash',
    qbo_account_type: null,
    qbo_account_sub_type: null,
    qbo_classification: null,
    values: valuesByNode.get(nodeId) || emptyValues(periodCount),
    is_total: rowType !== 'account',
    is_formula: rowType === 'formula',
    normal_balance: null,
  }))
}

export const forecastPeriodsFromSnapshot = (
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  grain: QuickBooksFpaForecastGrain | string,
  selectedEndPeriod: string | null | undefined,
  futureMonths: number,
): string[] => {
  const start = forecastStartIso(snapshot, selectedEndPeriod)
  if (!start) return []
  const horizonEnd = cashForecastHorizonEndIso(start)
  return grain === 'daily'
    ? Array.from({ length: daysBetweenIso(start, horizonEnd) }, (_value, index) => addDaysIso(start, index))
    : grain === 'weekly'
      ? Array.from({ length: Math.max(1, Math.ceil(daysBetweenIso(weekStartIso(start), horizonEnd) / 7)) }, (_value, index) => addDaysIso(weekStartIso(start), index * 7))
      : Array.from({ length: Math.max(1, futureMonths) }, (_value, index) => monthOffsetIso(monthStartIso(start), index) || monthStartIso(start))
}

export const buildDirectCashForecastShellFromSnapshot = (
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  grain: QuickBooksFpaForecastGrain | string,
  valueCase: QuickBooksFpaForecastValueCase | string,
  selectedEndPeriod: string | null | undefined,
  futureMonths: number,
): QuickBooksFpaMlStatementForecastResponse | null => {
  if (!snapshot.package) return null
  const periods = forecastPeriodsFromSnapshot(snapshot, grain, selectedEndPeriod, futureMonths)
  if (!periods.length) return null
  return {
    warnings: [],
    contract: buildClientForecastContract(snapshot, 'cash_flow', periods, {
      readiness: 'rebuilding',
      warnings: ['Forecast package metadata is loaded; daily cash rows are still warming.'],
    }),
    package: snapshot.package,
    grain,
    statement_type: 'cash_flow',
    value_case: valueCase,
    as_of_date: selectedEndPeriod || snapshot.as_of_date || null,
    statement: {
      connection_id: snapshot.package.connection_id,
      layout_version_id: snapshot.layout_nodes?.[0]?.layout_version_id || '',
      statement_type: 'cash_flow',
      balance_sheet_mode: 'activity',
      periods,
      rows: directCashStatementRowsFromValues(new Map(), periods.length),
      warnings: ['Forecast package metadata is loaded; daily cash rows are still warming.'],
    },
    validation: {
      ...(snapshot.validation || {}),
      status: 'warming',
      rollup_basis: snapshot.validation?.rollup_basis || 'materialized_parquet_artifacts',
      period_count: periods.length,
      included_net_cash_change: 0,
    },
    driver_assumptions: snapshot.driver_assumptions || {},
  }
}

export const buildDirectCashForecastFromSnapshot = (
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse | null,
  grain: QuickBooksFpaForecastGrain | string,
  valueCase: QuickBooksFpaForecastValueCase | string,
  selectedEndPeriod: string | null | undefined,
  futureMonths: number,
): QuickBooksFpaMlStatementForecastResponse | null => {
  if (!snapshot?.package || !snapshot.cash_daily?.length) return null
  const periods = forecastPeriodsFromSnapshot(snapshot, grain, selectedEndPeriod, futureMonths)
  if (!periods.length) return null
  const periodIndex = new Map(periods.map((period, index) => [period, index]))
  const valuesByNode = new Map<string, Array<number | null>>()
  const addValue = (nodeId: string, index: number, amount: number) => {
    const values = valuesByNode.get(nodeId) || emptyValues(periods.length)
    values[index] = Number(values[index] || 0) + amount
    if (Math.abs(Number(values[index] || 0)) <= 0.005) values[index] = null
    valuesByNode.set(nodeId, values)
  }
  for (const row of snapshot.cash_daily || []) {
    if (isDailyCashSummaryRow(row)) continue
    const period = periodStartForCashRow(String(row.forecast_date || ''), grain)
    const index = periodIndex.get(period)
    if (index == null) continue
    const amount = cashDailyValueForCase(row, valueCase)
    if (!Number.isFinite(amount) || Math.abs(amount) <= 0.005) continue
    const bucket = directCashBucketFromDaily(row, amount)
    addValue(bucket, index, amount)
    if (bucket === 'fg-forecast-cash-owner-equity') {
      addValue('fg-forecast-cash-equity', index, amount)
    } else {
      addValue(amount >= 0 ? 'fg-forecast-cash-receipts' : 'fg-forecast-cash-disbursements', index, amount)
    }
    addValue('fg-forecast-cash-net-change', index, amount)
  }
  const includedNetCashChange = (valuesByNode.get('fg-forecast-cash-net-change') || []).reduce<number>((sum, value) => sum + Number(value || 0), 0)
  return {
    warnings: [],
    contract: buildClientForecastContract(snapshot, 'cash_flow', periods),
    package: snapshot.package,
    grain,
    statement_type: 'cash_flow',
    value_case: valueCase,
    as_of_date: selectedEndPeriod || snapshot.as_of_date || null,
    statement: {
      connection_id: snapshot.package.connection_id,
      layout_version_id: snapshot.layout_nodes?.[0]?.layout_version_id || '',
      statement_type: 'cash_flow',
      balance_sheet_mode: 'activity',
      periods,
      rows: directCashStatementRowsFromValues(valuesByNode, periods.length),
      warnings: [],
    },
    validation: {
      ...(snapshot.validation || {}),
      rollup_basis: 'materialized_parquet_artifacts',
      period_count: periods.length,
      included_net_cash_change: includedNetCashChange,
    },
    driver_assumptions: snapshot.driver_assumptions || {},
  }
}

const allocateMonthlyDirectCashForecast = (
  monthlyForecast: QuickBooksFpaMlStatementForecastResponse | null,
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  grain: QuickBooksFpaForecastGrain | string,
  valueCase: QuickBooksFpaForecastValueCase | string,
  selectedEndPeriod: string | null | undefined,
  futureMonths: number,
): QuickBooksFpaMlStatementForecastResponse | null => {
  if (!monthlyForecast?.statement?.periods?.length || !snapshot.package || grain === 'month') return null
  const periods = forecastPeriodsFromSnapshot(snapshot, grain, selectedEndPeriod, futureMonths)
  if (!periods.length) return null
  const valuesByNode = new Map<string, Array<number | null>>()
  for (const sourceRow of monthlyForecast.statement.rows || []) {
    const targetValues = emptyValues(periods.length)
    monthlyForecast.statement.periods.forEach((monthPeriod, monthIndex) => {
      const monthStart = monthStartIso(monthPeriod)
      const monthEnd = monthOffsetIso(monthStart, 1) || addDaysIso(monthStart, daysInMonthIso(monthStart))
      const monthDays = daysInMonthIso(monthStart)
      const monthValue = Number(sourceRow.values?.[monthIndex] || 0)
      if (!Number.isFinite(monthValue) || Math.abs(monthValue) <= 0.005) return
      periods.forEach((period, periodIndex) => {
        const overlapDays = dateRangeOverlapDays(period, periodEndExclusiveIso(period, grain), monthStart, monthEnd)
        if (overlapDays <= 0) return
        targetValues[periodIndex] = Number(targetValues[periodIndex] || 0) + (monthValue * overlapDays / monthDays)
      })
    })
    if (hasStatementValue(targetValues)) valuesByNode.set(sourceRow.node_id, targetValues)
  }
  const includedNetCashChange = (valuesByNode.get('fg-forecast-cash-net-change') || []).reduce<number>((sum, value) => sum + Number(value || 0), 0)
  return {
    warnings: [],
    contract: buildClientForecastContract(snapshot, 'cash_flow', periods, {
      warnings: ['Daily and weekly periods are allocated from monthly forecast line metadata because materialized daily cash artifacts are not available for the selected reconciled date.'],
    }),
    package: snapshot.package,
    grain,
    statement_type: 'cash_flow',
    value_case: valueCase,
    as_of_date: selectedEndPeriod || snapshot.as_of_date || null,
    statement: {
      connection_id: snapshot.package.connection_id,
      layout_version_id: monthlyForecast.statement.layout_version_id || snapshot.layout_nodes?.[0]?.layout_version_id || '',
      statement_type: 'cash_flow',
      balance_sheet_mode: 'activity',
      periods,
      rows: directCashStatementRowsFromValues(valuesByNode, periods.length),
      warnings: [
        ...(monthlyForecast.statement.warnings || []),
        'Daily and weekly periods are allocated from monthly forecast line metadata because materialized daily cash artifacts are not available for the selected reconciled date.',
      ],
    },
    validation: {
      ...(snapshot.validation || {}),
      rollup_basis: 'legacy_monthly_line_metadata',
      monthly_line_backed: true,
      period_count: periods.length,
      included_net_cash_change: includedNetCashChange,
    },
    driver_assumptions: snapshot.driver_assumptions || {},
  }
}

export const isDirectCashForecast = (statement: QuickBooksFpaStatementRender | null | undefined): boolean =>
  Boolean(statement?.statement_type === 'cash_flow' && statement.rows?.some((row) => row.node_id === 'fg-forecast-cash-net-change'))

const directCashSourceBucket = (row: QuickBooksFpaStatementRow): { bucketId: string; sign: 1 | -1 } | null => {
  const accountLike = row.row_type === 'account' && (trimStr(row.account_id) || trimStr(row.account_number))
  if ((row.is_total || row.is_formula || row.row_type === 'heading' || row.row_type === 'group') && !accountLike) return null
  const text = [
    row.label,
    row.qbo_account_type,
    row.qbo_account_sub_type,
    row.qbo_classification,
    row.cash_flow_section,
  ].map((value) => String(value || '').toLowerCase()).join(' ').replace(/[_-]+/g, ' ')
  const isIncome = /\bincome\b|revenue|sales/.test(text) && !/expense|cost of goods|cogs|payroll|wage|salary/.test(text)
  const isOtherIncome = isIncome && /other income|interest income|misc/.test(text)
  if (isOtherIncome) return { bucketId: 'fg-forecast-cash-other-receipts', sign: 1 }
  if (isIncome) return { bucketId: 'fg-forecast-cash-customer-receipts', sign: 1 }
  const isExpense = /expense|cost of goods|cogs|cost of sales|payable|debit/.test(text) || row.normal_balance === 'debit'
  if (!isExpense && row.normal_balance === 'credit') return { bucketId: 'fg-forecast-cash-other-receipts', sign: 1 }
  if (!isExpense) return { bucketId: 'fg-forecast-cash-vendors', sign: -1 }
  if (/payroll|wage|salary|benefit|employee|contract labor/.test(text)) return { bucketId: 'fg-forecast-cash-payroll', sign: -1 }
  if (/tax|debt|loan|interest expense|principal|depreciation|amortization|capex|capital|equipment|fixed asset/.test(text)) return { bucketId: 'fg-forecast-cash-debt-tax-capex', sign: -1 }
  return { bucketId: 'fg-forecast-cash-vendors', sign: -1 }
}

const statementRowText = (row: QuickBooksFpaStatementRow): string => [
  row.label,
  row.account_number,
  row.qbo_account_type,
  row.qbo_account_sub_type,
  row.qbo_classification,
  row.cash_flow_section,
].map((value) => String(value || '').toLowerCase()).join(' ').replace(/[_-]+/g, ' ')

const isGeneratedStatementSubtotalRow = (row: QuickBooksFpaStatementRow): boolean => {
  const nodeId = String(row.node_id || '')
  if (nodeId.endsWith(':subtotal')) return true
  return row.row_type === 'group' && /^total\b/i.test(String(row.label || '').trim())
}

const statementParentIdsExcludingGeneratedSubtotals = (
  rows: QuickBooksFpaStatementRow[] | null | undefined,
): Set<string> => new Set(
  (rows || [])
    .filter((row) => !isGeneratedStatementSubtotalRow(row))
    .map((row) => row.parent_node_id)
    .filter((id): id is string => Boolean(id)),
)

const balanceSheetMovementBucket = (row: QuickBooksFpaStatementRow): { bucketId: string; sign: 1 | -1; labelPrefix: string } | null => {
  const accountLike = row.row_type === 'account' && hasStatementAccountIdentity(row)
  if ((row.is_total || row.is_formula || row.row_type === 'heading' || row.row_type === 'group') && !accountLike) return null
  const text = statementRowText(row)
  const classification = String(row.qbo_classification || '').toLowerCase()
  const accountType = String(row.qbo_account_type || '').toLowerCase()
  const isCash = /\bcash\b|bank|checking|savings|money market/.test(text)
  if (isCash) return null
  const isAr = /accounts receivable|receivable|\bar\b|trade debtor/.test(text)
  if (isAr) return { bucketId: 'fg-forecast-cash-customer-receipts', sign: -1, labelPrefix: 'A/R movement' }
  const isPayrollRelated = /payroll|wage|salary|employee|benefit|withholding|fica|medicare|social security|workers comp|contract labor/.test(text)
  const isAp = /accounts payable|payable|\bap\b|accrued expense|accrual/.test(text)
  if (isAp) {
    return {
      bucketId: isPayrollRelated ? 'fg-forecast-cash-payroll' : 'fg-forecast-cash-vendors',
      sign: 1,
      labelPrefix: isPayrollRelated ? 'Payroll liability movement' : 'A/P movement',
    }
  }
  const isLiability = classification === 'liability' || accountType.includes('liability') || /credit card|loan|note payable|line of credit|tax payable|sales tax|debt/.test(text)
  if (isLiability) {
    return {
      bucketId: isPayrollRelated ? 'fg-forecast-cash-payroll' : 'fg-forecast-cash-debt-tax-capex',
      sign: 1,
      labelPrefix: isPayrollRelated ? 'Payroll liability movement' : 'Liability movement',
    }
  }
  const isEquity = classification === 'equity' || accountType.includes('equity') || /owner|member|partner|draw|distribution|contribution|equity/.test(text)
  if (isEquity) {
    if (/retained earnings|current net income|net income|opening balance|prior period/.test(text)) return null
    return { bucketId: 'fg-forecast-cash-owner-equity', sign: 1, labelPrefix: 'Equity movement' }
  }
  const isAsset = classification === 'asset' || accountType.includes('asset') || /prepaid|deposit|inventory|fixed asset|equipment|property|intangible|capital/.test(text)
  if (isAsset) return { bucketId: 'fg-forecast-cash-debt-tax-capex', sign: -1, labelPrefix: 'Asset movement' }
  return {
    bucketId: 'fg-forecast-cash-debt-tax-capex',
    sign: row.normal_balance === 'credit' ? 1 : -1,
    labelPrefix: 'Other balance sheet movement',
  }
}

const balanceSheetMovementValues = (
  row: QuickBooksFpaStatementRow,
  actualCount: number,
  sign: 1 | -1,
): Array<number | null> => {
  const values = row.values || []
  const firstCurrentIndex = values.length - actualCount
  if (actualCount <= 0 || firstCurrentIndex < 0) return emptyValues(actualCount)
  return Array.from({ length: actualCount }, (_value, index) => {
    const currentIndex = firstCurrentIndex + index
    const priorIndex = currentIndex - 1
    if (priorIndex < 0) return null
    const current = Number(values[currentIndex])
    const prior = Number(values[priorIndex])
    if (!Number.isFinite(current) || !Number.isFinite(prior)) return null
    const impact = (current - prior) * sign
    return Math.abs(impact) > 0.005 ? impact : null
  })
}

const balanceSheetAdjustmentValues = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  row: QuickBooksFpaStatementRow,
  actualCount: number,
  sign: 1 | -1,
): Array<number | null> => {
  if (statement?.balance_sheet_mode === 'activity') {
    return row.values.slice(-actualCount).map((value) => {
      const numeric = Number(value)
      return Number.isFinite(numeric) && Math.abs(numeric) > 0.005 ? numeric : null
    })
  }
  return balanceSheetMovementValues(row, actualCount, sign)
}

const signedDetailActivityValues = (values: Array<number | null | undefined>): Array<number | null> =>
  values.map((value) => {
    const next = Number(value || 0)
    return Number.isFinite(next) && Math.abs(next) > 0.005 ? next : null
  })

const balanceSheetCashRow = (statement: QuickBooksFpaStatementRender | null | undefined): QuickBooksFpaStatementRow | null => {
  const rows = statement?.rows || []
  const cashRows = rows.filter((row) => {
    const text = statementRowText(row)
    return /\bcash\b|bank|checking|savings|money market/.test(text)
  })
  return cashRows.find((row) => /^total bank accounts$/i.test(row.label || ''))
    || cashRows.find((row) => /^bank accounts$/i.test(row.label || '') && (row.is_total || row.is_formula))
    || cashRows.find((row) => /total cash|cash and cash equivalents|cash$/i.test(row.label || ''))
    || cashRows.find((row) => row.is_total || row.is_formula)
    || cashRows[0]
    || null
}

export const balanceSheetCashValues = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  actualCount: number,
): Array<number | null> => {
  const row = balanceSheetCashRow(statement)
  if (!row || actualCount <= 0) return emptyValues(actualCount)
  return row.values.slice(-actualCount).map((value) => {
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
  })
}

const balanceSheetBeginningCashValues = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  actualCount: number,
): Array<number | null> => {
  const row = balanceSheetCashRow(statement)
  const values = row?.values || []
  const firstCurrentIndex = values.length - actualCount
  if (!row || actualCount <= 0 || firstCurrentIndex < 0) return emptyValues(actualCount)
  return Array.from({ length: actualCount }, (_value, index) => {
    const prior = Number(values[firstCurrentIndex + index - 1])
    return Number.isFinite(prior) ? prior : null
  })
}

const statementLineValues = (
  statement: QuickBooksFpaStatementRender | null | undefined,
  labelPattern: RegExp,
  count: number,
): Array<number | null> | null => {
  const values = statement?.rows?.find((row) => labelPattern.test(row.label || ''))?.values.slice(-count)
  if (!values?.length) return null
  const normalized = values.map((value) => {
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
  })
  return hasStatementValue(normalized) ? normalized : null
}

const firstStatementValueVector = (
  count: number,
  ...vectors: Array<Array<number | null> | null | undefined>
): Array<number | null> => {
  const values = vectors.find((candidate) => candidate?.length === count && hasStatementValue(candidate))
  return values || emptyValues(count)
}

const latestFiniteValue = (...vectors: Array<Array<number | null> | null | undefined>): number | null => {
  for (const values of vectors) {
    const reversed = [...(values || [])].reverse()
    const value = reversed.find((candidate) => Number.isFinite(Number(candidate)))
    if (value != null) return Number(value)
  }
  return null
}

const rollCashBalancesBackFromAnchor = (
  netValues: Array<number | null | undefined>,
  endingCashAtAnchor: number | null | undefined,
): { beginning: Array<number | null>; ending: Array<number | null> } | null => {
  const anchor = Number(endingCashAtAnchor)
  if (!Number.isFinite(anchor) || !netValues.length) return null
  const beginning = emptyValues(netValues.length)
  const ending = emptyValues(netValues.length)
  let rollingEnding = anchor
  for (let index = netValues.length - 1; index >= 0; index -= 1) {
    const net = Number(netValues[index] || 0)
    ending[index] = rollingEnding
    beginning[index] = rollingEnding - net
    if (Number.isFinite(beginning[index])) rollingEnding = Number(beginning[index])
  }
  return { beginning, ending }
}

type CashSourceBasis = 'pnl' | 'balance_sheet'

const sourceBasisGroupId = (bucketId: string, basis: CashSourceBasis): string =>
  `${bucketId}:basis:${basis}`

const sourceBasisGroupLabel = (basis: CashSourceBasis): string =>
  basis === 'pnl' ? 'P&L line items' : 'Balance sheet adjustments'

const sourceChildRow = (
  source: QuickBooksFpaStatementRow,
  bucketId: string,
  basis: CashSourceBasis,
  actualValues: Array<number | null>,
  forecastValues: Array<number | null>,
  labelPrefix?: string,
): QuickBooksFpaStatementRow => ({
  ...source,
  node_id: `${sourceBasisGroupId(bucketId, basis)}:source:${source.node_id}`,
  parent_node_id: sourceBasisGroupId(bucketId, basis),
  row_type: 'account',
  statement_type: 'cash_flow',
  level: 3,
  label: labelPrefix ? `${labelPrefix} · ${source.label}` : source.label,
  cash_flow_section: basis === 'pnl' ? 'pnl_line_item' : 'balance_sheet_adjustment',
  values: [...actualValues, ...forecastValues],
  is_total: false,
  is_formula: false,
})

const pnlSourceLeafRow = (
  source: QuickBooksFpaStatementRow,
  actualValues: Array<number | null>,
  forecastValues: Array<number | null>,
): QuickBooksFpaStatementRow => ({
  ...source,
  values: [...actualValues, ...forecastValues],
  is_total: false,
  is_formula: false,
})

const syntheticSourceRow = (
  nodeId: string,
  label: string,
  bucketId: string,
  basis: CashSourceBasis,
  values: Array<number | null>,
): QuickBooksFpaStatementRow => ({
  node_id: `${sourceBasisGroupId(bucketId, basis)}:source:${nodeId}`,
  parent_node_id: sourceBasisGroupId(bucketId, basis),
  row_type: 'account',
  statement_type: 'cash_flow',
  label,
  level: 3,
  account_id: null,
  account_number: null,
  cash_flow_section: basis === 'pnl' ? 'pnl_line_item' : 'balance_sheet_adjustment',
  qbo_account_type: null,
  qbo_account_sub_type: null,
  qbo_classification: null,
  normal_balance: null,
  values,
  is_total: false,
  is_formula: false,
})

const sourceBasisGroupRow = (
  bucketId: string,
  basis: CashSourceBasis,
  children: QuickBooksFpaStatementRow[],
  periodCount: number,
): QuickBooksFpaStatementRow => ({
  node_id: sourceBasisGroupId(bucketId, basis),
  parent_node_id: bucketId,
  row_type: 'group',
  statement_type: 'cash_flow',
  label: sourceBasisGroupLabel(basis),
  level: 2,
  account_id: null,
  account_number: null,
  cash_flow_section: basis === 'pnl' ? 'pnl_line_item' : 'balance_sheet_adjustment',
  qbo_account_type: null,
  qbo_account_sub_type: null,
  qbo_classification: null,
  normal_balance: null,
  values: children.length ? addVectors(...children.map((row) => row.values)) : emptyValues(periodCount),
  is_total: true,
  is_formula: false,
})

const sourceBasisRows = (
  sourceRowsByBucket: Map<string, QuickBooksFpaStatementRow[]>,
  bucketId: string,
  basis: CashSourceBasis,
  periodCount: number,
): QuickBooksFpaStatementRow[] => {
  const groupId = sourceBasisGroupId(bucketId, basis)
  const rows = sourceRowsByBucket.get(bucketId) || []
  const children = rows.filter((row) => row.parent_node_id === groupId)
  if (!rows.length || !children.length) return []
  return [sourceBasisGroupRow(bucketId, basis, children, periodCount), ...rows]
}

const sourceRowsForBucket = (
  pnlSourceRowsByBucket: Map<string, QuickBooksFpaStatementRow[]>,
  balanceSheetSourceRowsByBucket: Map<string, QuickBooksFpaStatementRow[]>,
  bucketId: string,
  periodCount: number,
): QuickBooksFpaStatementRow[] => [
  ...sourceBasisRows(pnlSourceRowsByBucket, bucketId, 'pnl', periodCount),
  ...sourceBasisRows(balanceSheetSourceRowsByBucket, bucketId, 'balance_sheet', periodCount),
]

const buildNestedPnlSourceRows = (
  sourceStatement: QuickBooksFpaStatementRender | null | undefined,
  bucketId: string,
  leafRows: QuickBooksFpaStatementRow[],
  periodCount: number,
): QuickBooksFpaStatementRow[] => {
  if (!leafRows.length) return []
  const rows = sourceStatement?.rows || []
  const rowsById = new Map(rows.map((row) => [row.node_id, row]))
  const leafRowsById = new Map(leafRows.map((row) => [row.node_id, row]))
  const includedSourceIds = new Set<string>()
  const valuesBySourceId = new Map<string, Array<number | null>>()

  const parentChain = (row: QuickBooksFpaStatementRow): string[] => {
    const chain: string[] = []
    const visited = new Set<string>([row.node_id])
    let parentId = row.parent_node_id || null
    while (parentId) {
      if (visited.has(parentId)) break
      visited.add(parentId)
      const parent = rowsById.get(parentId)
      if (!parent) break
      chain.push(parent.node_id)
      parentId = parent.parent_node_id || null
    }
    return chain
  }

  for (const leaf of leafRows) {
    includedSourceIds.add(leaf.node_id)
    addVectorInto(valuesBySourceId, leaf.node_id, leaf.values, periodCount)
    for (const ancestorId of parentChain(leaf)) {
      includedSourceIds.add(ancestorId)
      addVectorInto(valuesBySourceId, ancestorId, leaf.values, periodCount)
    }
  }

  const groupId = sourceBasisGroupId(bucketId, 'pnl')
  const sourceNodeId = (sourceId: string) => `${groupId}:source:${sourceId}`
  const includedParentId = (row: QuickBooksFpaStatementRow): string | null => {
    let parentId = row.parent_node_id || null
    const visited = new Set<string>([row.node_id])
    while (parentId) {
      if (visited.has(parentId)) break
      visited.add(parentId)
      if (includedSourceIds.has(parentId)) return parentId
      parentId = rowsById.get(parentId)?.parent_node_id || null
    }
    return null
  }
  const includedDepth = (row: QuickBooksFpaStatementRow): number => {
    let depth = 0
    let parentId = includedParentId(row)
    const visited = new Set<string>([row.node_id])
    while (parentId) {
      if (visited.has(parentId)) break
      visited.add(parentId)
      depth += 1
      parentId = includedParentId(rowsById.get(parentId) || { node_id: parentId } as QuickBooksFpaStatementRow)
    }
    return depth
  }

  return rows
    .filter((row) => includedSourceIds.has(row.node_id))
    .map((row) => {
      const leaf = leafRowsById.get(row.node_id)
      const parentId = includedParentId(row)
      const hasChildren = rows.some((candidate) => candidate.parent_node_id === row.node_id && includedSourceIds.has(candidate.node_id))
      const rowValues = valuesBySourceId.get(row.node_id) || emptyValues(periodCount)
      return {
        ...row,
        node_id: sourceNodeId(row.node_id),
        parent_node_id: parentId ? sourceNodeId(parentId) : groupId,
        row_type: leaf && !hasChildren ? 'account' : row.row_type === 'heading' ? 'group' : row.row_type,
        statement_type: 'cash_flow',
        level: 3 + includedDepth(row),
        cash_flow_section: 'pnl_line_item',
        values: rowValues,
        is_total: !leaf || hasChildren,
        is_formula: false,
      }
    })
}

const directCashRollupRow = (
  forecastRowsById: Map<string, QuickBooksFpaStatementRow>,
  args: {
    nodeId: string
    label: string
    parentNodeId?: string | null
    rowType: string
    level: number
    actualValues: Array<number | null>
    forecastValues?: Array<number | null>
  },
): QuickBooksFpaStatementRow => {
  const source = forecastRowsById.get(args.nodeId)
  return {
    ...(source || {
      account_id: null,
      account_number: null,
      qbo_account_type: null,
      qbo_account_sub_type: null,
      qbo_classification: null,
      normal_balance: null,
    }),
    node_id: args.nodeId,
    parent_node_id: args.parentNodeId || null,
    row_type: args.rowType,
    statement_type: 'cash_flow',
    label: args.label,
    level: args.level,
    cash_flow_section: 'direct_cash',
    values: [...args.actualValues, ...(args.forecastValues || source?.values || [])],
    is_total: args.rowType !== 'account',
    is_formula: args.rowType === 'formula',
  }
}

export const buildMappedDirectCashStatement = (
  actualCashFlow: QuickBooksFpaStatementRender | null,
  actualBasis: QuickBooksFpaStatementRender | null,
  actualBalanceSheet: QuickBooksFpaStatementRender | null,
  forecast: QuickBooksFpaStatementRender,
  historyMonths: number,
  futureMonths: number,
  actualBalanceSheetSource: QuickBooksFpaStatementRender | null = null,
): (QuickBooksFpaStatementRender & { period_kinds?: string[] }) => {
  const actualPeriods = (actualCashFlow?.periods?.length ? actualCashFlow.periods : actualBasis?.periods || []).slice(-Math.max(0, historyMonths))
  const forecastPeriods = forecast.periods.slice(0, Math.max(1, futureMonths))
  const actualCount = actualPeriods.length
  const forecastCount = forecastPeriods.length
  const forecastRowsById = new Map(forecast.rows.map((row) => [row.node_id, cloneRowWithValues(row, row.values.slice(0, forecastCount))]))
  const directActualRowsById = isDirectCashForecast(actualCashFlow)
    ? new Map((actualCashFlow?.rows || []).map((row) => [row.node_id, cloneRowWithValues(row, row.values.slice(-actualCount))]))
    : new Map<string, QuickBooksFpaStatementRow>()
  const directActualValues = (nodeId: string): Array<number | null> | null => {
    const values = directActualRowsById.get(nodeId)?.values || []
    return values.length && hasStatementValue(values) ? values : null
  }
  const useDirectActualRows = directActualRowsById.size > 0
  const pnlLeafRowsByBucket = new Map<string, QuickBooksFpaStatementRow[]>()
  const balanceSheetSourceRowsByBucket = new Map<string, QuickBooksFpaStatementRow[]>()
  const sourceParentIds = statementParentIdsExcludingGeneratedSubtotals(actualBasis?.rows)
  const balanceSheetSourceStatement = actualBalanceSheetSource || actualBalanceSheet
  let mappedPnlAccountRows = 0
  let mappedBalanceSheetRows = 0
  let excludedBalanceSheetRows = 0
  if (!useDirectActualRows) {
    for (const row of actualBasis?.rows || []) {
      if (sourceParentIds.has(row.node_id) && !hasStatementAccountIdentity(row)) continue
      const bucket = directCashSourceBucket(row)
      if (!bucket) continue
      const actualValues = signedDetailActivityValues(row.values.slice(-actualCount))
      if (!hasStatementValue(actualValues)) continue
      const bucketRows = pnlLeafRowsByBucket.get(bucket.bucketId) || []
      bucketRows.push(pnlSourceLeafRow(row, actualValues, emptyValues(forecastCount)))
      pnlLeafRowsByBucket.set(bucket.bucketId, bucketRows)
      mappedPnlAccountRows += 1
    }
    for (const row of balanceSheetSourceStatement?.rows || []) {
      const bucket = balanceSheetMovementBucket(row)
      if (!bucket) {
        if (!row.is_total && !row.is_formula && row.row_type !== 'heading' && row.row_type !== 'group') excludedBalanceSheetRows += 1
        continue
      }
      const actualValues = balanceSheetAdjustmentValues(balanceSheetSourceStatement, row, actualCount, bucket.sign)
      if (!hasStatementValue(actualValues)) continue
      const bucketRows = balanceSheetSourceRowsByBucket.get(bucket.bucketId) || []
      bucketRows.push(sourceChildRow(row, bucket.bucketId, 'balance_sheet', actualValues, emptyValues(forecastCount), bucket.labelPrefix))
      balanceSheetSourceRowsByBucket.set(bucket.bucketId, bucketRows)
      mappedBalanceSheetRows += 1
    }
  }
  const sourceValueRows = (bucketId: string) => [
    ...(pnlLeafRowsByBucket.get(bucketId) || []),
    ...(balanceSheetSourceRowsByBucket.get(bucketId) || []),
  ]
  const bucketActualValues = (bucketId: string) => addVectors(...sourceValueRows(bucketId).map((row) => row.values.slice(0, actualCount)))
  const bucketForecastValues = (bucketId: string) => forecastRowsById.get(bucketId)?.values.slice(0, forecastCount) || emptyValues(forecastCount)
  const allocateForecastToSources = (bucketId: string) => {
    const rows = sourceValueRows(bucketId)
    if (!rows.length) return
    const weights = rows.map((row) => row.values.slice(0, actualCount).reduce<number>((sum, value) => sum + Math.abs(Number(value || 0)), 0))
    const totalWeight = weights.reduce<number>((sum, value) => sum + value, 0)
    const forecastValues = bucketForecastValues(bucketId)
    rows.forEach((row, index) => {
      const share = totalWeight > 0 ? weights[index] / totalWeight : 1 / rows.length
      row.values = [...row.values.slice(0, actualCount), ...scaleVector(forecastValues, share)]
    })
  }
  [
    'fg-forecast-cash-customer-receipts',
    'fg-forecast-cash-other-receipts',
    'fg-forecast-cash-payroll',
    'fg-forecast-cash-vendors',
    'fg-forecast-cash-debt-tax-capex',
    'fg-forecast-cash-other-disbursements',
    'fg-forecast-cash-owner-equity',
  ].forEach(allocateForecastToSources)

  const customerActual = directActualValues('fg-forecast-cash-customer-receipts') || bucketActualValues('fg-forecast-cash-customer-receipts')
  const otherReceiptActual = directActualValues('fg-forecast-cash-other-receipts') || bucketActualValues('fg-forecast-cash-other-receipts')
  const payrollActual = directActualValues('fg-forecast-cash-payroll') || bucketActualValues('fg-forecast-cash-payroll')
  const vendorActual = directActualValues('fg-forecast-cash-vendors') || bucketActualValues('fg-forecast-cash-vendors')
  let debtTaxActual = directActualValues('fg-forecast-cash-debt-tax-capex') || bucketActualValues('fg-forecast-cash-debt-tax-capex')
  const otherDisbursementActual = directActualValues('fg-forecast-cash-other-disbursements') || bucketActualValues('fg-forecast-cash-other-disbursements')
  const equityActual = directActualValues('fg-forecast-cash-owner-equity') || bucketActualValues('fg-forecast-cash-owner-equity')
  const receiptActual = directActualValues('fg-forecast-cash-receipts') || addVectors(customerActual, otherReceiptActual)
  const directDisbursementActual = directActualValues('fg-forecast-cash-disbursements')
  let actualEndingCash = firstStatementValueVector(
    actualCount,
    directActualValues('fg-forecast-cash-ending-cash'),
    balanceSheetCashValues(actualBalanceSheet, actualCount),
    statementLineValues(actualCashFlow, /^ending cash$/i, actualCount),
  )
  let actualBeginningCash = firstStatementValueVector(
    actualCount,
    directActualValues('fg-forecast-cash-beginning-cash'),
    balanceSheetBeginningCashValues(actualBalanceSheet, actualCount),
    statementLineValues(actualCashFlow, /^beginning cash$/i, actualCount),
  )
  const preliminaryDisbursementActual = addVectors(payrollActual, vendorActual, debtTaxActual, otherDisbursementActual)
  const preliminaryNetActual = addVectors(receiptActual, preliminaryDisbursementActual, equityActual)
  const hasPeriodAlignedActualCash = actualEndingCash.length === actualCount
    && actualBeginningCash.length === actualCount
    && hasStatementValue(actualEndingCash)
    && hasStatementValue(actualBeginningCash)
  const cashRollForwardActual = hasPeriodAlignedActualCash ? actualEndingCash.map((ending, index) => {
    const beginning = Number(actualBeginningCash[index])
    const endingValue = Number(ending)
    if (!Number.isFinite(beginning) || !Number.isFinite(endingValue)) return null
    const change = endingValue - beginning
    return Math.abs(change) > 0.005 ? change : null
  }) : emptyValues(actualCount)
  const cashReconciliationActual = hasPeriodAlignedActualCash ? addVectors(cashRollForwardActual, scaleVector(preliminaryNetActual, -1)) : emptyValues(actualCount)
  if (!useDirectActualRows && hasStatementValue(cashReconciliationActual)) {
    const bucketRows = balanceSheetSourceRowsByBucket.get('fg-forecast-cash-debt-tax-capex') || []
    bucketRows.push(syntheticSourceRow(
      'cash-reconciliation-adjustment',
      'Cash reconciliation adjustment',
      'fg-forecast-cash-debt-tax-capex',
      'balance_sheet',
      [...cashReconciliationActual, ...emptyValues(forecastCount)],
    ))
    balanceSheetSourceRowsByBucket.set('fg-forecast-cash-debt-tax-capex', bucketRows)
    debtTaxActual = addVectors(debtTaxActual, cashReconciliationActual)
  }
  const pnlSourceRowsByBucket = new Map<string, QuickBooksFpaStatementRow[]>()
  for (const [bucketId, leafRows] of pnlLeafRowsByBucket) {
    pnlSourceRowsByBucket.set(bucketId, buildNestedPnlSourceRows(actualBasis, bucketId, leafRows, actualCount + forecastCount))
  }
  const disbursementActual = directDisbursementActual || addVectors(payrollActual, vendorActual, debtTaxActual, otherDisbursementActual)
  const netActual = directActualValues('fg-forecast-cash-net-change') || addVectors(receiptActual, disbursementActual, equityActual)
  if (!hasPeriodAlignedActualCash && hasStatementValue(netActual)) {
    const detailCashBalances = rollCashBalancesBackFromAnchor(
      netActual,
      latestFiniteValue(
        directActualValues('fg-forecast-cash-ending-cash'),
        balanceSheetCashValues(actualBalanceSheet, actualBalanceSheet?.periods?.length || 0),
        statementLineValues(actualCashFlow, /^ending cash$/i, actualCashFlow?.periods?.length || 0),
      ),
    )
    if (detailCashBalances) {
      actualBeginningCash = detailCashBalances.beginning
      actualEndingCash = detailCashBalances.ending
    }
  }
  const netForecast = bucketForecastValues('fg-forecast-cash-net-change')
  const forecastBeginningCash = emptyValues(forecastCount)
  const forecastEndingCash = emptyValues(forecastCount)
  let rollingCash = Number(actualEndingCash[actualEndingCash.length - 1] || 0)
  for (let index = 0; index < forecastCount; index += 1) {
    forecastBeginningCash[index] = Number.isFinite(rollingCash) ? rollingCash : null
    rollingCash += Number(netForecast[index] || 0)
    forecastEndingCash[index] = Number.isFinite(rollingCash) ? rollingCash : null
  }
  const rows: QuickBooksFpaStatementRow[] = [
    directCashRollupRow(forecastRowsById, { nodeId: 'fg-forecast-cash-receipts', label: 'Receipts', rowType: 'group', level: 0, actualValues: receiptActual }),
    directCashRollupRow(forecastRowsById, { nodeId: 'fg-forecast-cash-customer-receipts', label: 'Customer receipts', parentNodeId: 'fg-forecast-cash-receipts', rowType: 'group', level: 1, actualValues: customerActual }),
    ...sourceRowsForBucket(pnlSourceRowsByBucket, balanceSheetSourceRowsByBucket, 'fg-forecast-cash-customer-receipts', actualCount + forecastCount),
    directCashRollupRow(forecastRowsById, { nodeId: 'fg-forecast-cash-other-receipts', label: 'Other receipts', parentNodeId: 'fg-forecast-cash-receipts', rowType: 'group', level: 1, actualValues: otherReceiptActual }),
    ...sourceRowsForBucket(pnlSourceRowsByBucket, balanceSheetSourceRowsByBucket, 'fg-forecast-cash-other-receipts', actualCount + forecastCount),
    directCashRollupRow(forecastRowsById, { nodeId: 'fg-forecast-cash-disbursements', label: 'Disbursements', rowType: 'group', level: 0, actualValues: disbursementActual }),
    directCashRollupRow(forecastRowsById, { nodeId: 'fg-forecast-cash-payroll', label: 'Payroll and people costs', parentNodeId: 'fg-forecast-cash-disbursements', rowType: 'group', level: 1, actualValues: payrollActual }),
    ...sourceRowsForBucket(pnlSourceRowsByBucket, balanceSheetSourceRowsByBucket, 'fg-forecast-cash-payroll', actualCount + forecastCount),
    directCashRollupRow(forecastRowsById, { nodeId: 'fg-forecast-cash-vendors', label: 'Vendors and operating expenses', parentNodeId: 'fg-forecast-cash-disbursements', rowType: 'group', level: 1, actualValues: vendorActual }),
    ...sourceRowsForBucket(pnlSourceRowsByBucket, balanceSheetSourceRowsByBucket, 'fg-forecast-cash-vendors', actualCount + forecastCount),
    directCashRollupRow(forecastRowsById, { nodeId: 'fg-forecast-cash-debt-tax-capex', label: 'Debt, tax, and capital items', parentNodeId: 'fg-forecast-cash-disbursements', rowType: 'group', level: 1, actualValues: debtTaxActual }),
    ...sourceRowsForBucket(pnlSourceRowsByBucket, balanceSheetSourceRowsByBucket, 'fg-forecast-cash-debt-tax-capex', actualCount + forecastCount),
    directCashRollupRow(forecastRowsById, { nodeId: 'fg-forecast-cash-other-disbursements', label: 'Other disbursements', parentNodeId: 'fg-forecast-cash-disbursements', rowType: 'group', level: 1, actualValues: otherDisbursementActual }),
    ...sourceRowsForBucket(pnlSourceRowsByBucket, balanceSheetSourceRowsByBucket, 'fg-forecast-cash-other-disbursements', actualCount + forecastCount),
    directCashRollupRow(forecastRowsById, { nodeId: 'fg-forecast-cash-equity', label: 'Changes in equity', rowType: 'group', level: 0, actualValues: equityActual }),
    directCashRollupRow(forecastRowsById, { nodeId: 'fg-forecast-cash-owner-equity', label: 'Owner draws and contributions', parentNodeId: 'fg-forecast-cash-equity', rowType: 'group', level: 1, actualValues: equityActual }),
    ...sourceRowsForBucket(pnlSourceRowsByBucket, balanceSheetSourceRowsByBucket, 'fg-forecast-cash-owner-equity', actualCount + forecastCount),
    directCashRollupRow(forecastRowsById, { nodeId: 'fg-forecast-cash-net-change', label: 'Net cash change', rowType: 'formula', level: 0, actualValues: netActual }),
    directCashRollupRow(forecastRowsById, { nodeId: 'fg-forecast-cash-beginning-cash', label: 'Beginning cash', rowType: 'formula', level: 0, actualValues: actualBeginningCash, forecastValues: forecastBeginningCash }),
    directCashRollupRow(forecastRowsById, { nodeId: 'fg-forecast-cash-ending-cash', label: 'Ending cash', rowType: 'formula', level: 0, actualValues: actualEndingCash, forecastValues: forecastEndingCash }),
  ]
  return {
    ...forecast,
    periods: [...actualPeriods, ...forecastPeriods],
    rows,
    warnings: [
      ...(forecast.warnings || []),
      useDirectActualRows
        ? `Cash bridge actuals loaded from detail-grain GL rows across ${actualCount} period(s).`
        : `Cash bridge reconciliation: mapped ${mappedPnlAccountRows} P&L account row(s), mapped ${mappedBalanceSheetRows} balance-sheet adjustment row(s), intentionally excluded ${excludedBalanceSheetRows} balance-sheet cash/current-earnings row(s).`,
      ...(!useDirectActualRows && hasStatementValue(cashReconciliationActual)
        ? [`Cash bridge reconciliation adjustment included so actual history ties to beginning and ending cash.`]
        : []),
    ],
    period_kinds: [...actualPeriods.map(() => 'actual'), ...forecastPeriods.map(() => 'forecast')],
  }
	}

export const duckDbPreviewRowsAsObjects = (result: any): any[] => {
  if (Array.isArray(result?.rows) && result.rows.every((row: any) => row && !Array.isArray(row) && typeof row === 'object')) return result.rows
  const headers = Array.isArray(result?.headers) ? result.headers.map((header: any) => String(header || '')) : []
  const rows = Array.isArray(result?.rows)
    ? result.rows
    : Array.isArray(result?.previewRows)
      ? result.previewRows
      : Array.isArray(result?.preview_rows)
        ? result.preview_rows
        : []
  if (!headers.length || !Array.isArray(rows)) return []
  return rows.map((row: any) => {
    if (row && !Array.isArray(row) && typeof row === 'object') return row
    const out: Record<string, any> = {}
    headers.forEach((header: string, index: number) => {
      out[header] = Array.isArray(row) ? row[index] : undefined
    })
    return out
  })
}

const previewRowsAsObjects = duckDbPreviewRowsAsObjects

const rowValue = (row: any, ...keys: string[]): any => {
  for (const key of keys) {
    if (row?.[key] !== undefined) return row[key]
    const found = Object.keys(row || {}).find((candidate) => candidate.toLowerCase() === key.toLowerCase())
    if (found) return row[found]
  }
  return undefined
}

const snapshotArtifactRowCount = (
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse | null | undefined,
  artifactKey: string,
): number => Number(snapshot?.artifact_materialization?.artifacts?.[artifactKey]?.rows ?? 0)

const snapshotHasArtifactRows = (
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse | null | undefined,
  artifactKey: string,
): boolean => {
  const rows = snapshotArtifactRowCount(snapshot, artifactKey)
  return Boolean(snapshot?.artifact_urls?.[artifactKey] && Number.isFinite(rows) && rows > 0)
}

export const snapshotSupportsMonthlyLineMetadata = (snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse | null | undefined): boolean => {
  if (!snapshot?.package?.id) return false
  const hasEmbeddedRows = snapshot?.validation?.monthly_line_backed === true
    && (snapshot?.line_metadata?.length || 0) > 0
    && (snapshot?.layout_nodes?.length || 0) > 0
  const hasArtifactRows = snapshotHasArtifactRows(snapshot, 'line_metadata')
    && snapshotHasArtifactRows(snapshot, 'layout_node')
  return hasEmbeddedRows || hasArtifactRows
}

export const loadForecastCashDailyFromDuckDb = async (
  workerClient: AppWorkerClient | null | undefined,
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  duckDbOpts?: Record<string, any> | null,
): Promise<QuickBooksFpaMlCashDailyForecast[]> => {
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') return []
  const packageId = snapshot.package?.id || ''
  const scenarioKey = snapshot.scenario_key || snapshot.package?.active_scenario_key || 'base'
  const result = await workerClient.duckdb_run_sql(`
    SELECT
      forecast_date,
      cash_event_type,
      source_type,
      source_id,
      counterparty_name,
      expected_amount,
      low_amount,
      high_amount,
      confidence,
      evidence_json
    FROM fg_qbo_fpa_forecast_cash_daily
    WHERE CAST(package_id AS VARCHAR) = '${packageId.replace(/'/g, "''")}'
      AND COALESCE(CAST(scenario_key AS VARCHAR), 'base') = '${scenarioKey.replace(/'/g, "''")}'
    ORDER BY forecast_date, cash_event_type, source_key
  `, {
    ...(duckDbOpts || {}),
    previewRowLimit: 50000,
    timeoutMs: 60000,
    caller: 'QuickBooksCfoCore:forecast_cash_daily_query',
  })
  return previewRowsAsObjects(result).map((row) => ({
    forecast_date: String(rowValue(row, 'forecast_date') || ''),
    cash_event_type: String(rowValue(row, 'cash_event_type') || ''),
    source_type: String(rowValue(row, 'source_type') || ''),
    source_id: String(rowValue(row, 'source_id') || ''),
    counterparty_name: String(rowValue(row, 'counterparty_name') || ''),
    expected_amount: Number(rowValue(row, 'expected_amount') || 0),
    low_amount: rowValue(row, 'low_amount') == null ? null : Number(rowValue(row, 'low_amount')),
    high_amount: rowValue(row, 'high_amount') == null ? null : Number(rowValue(row, 'high_amount')),
    confidence: rowValue(row, 'confidence') == null ? null : Number(rowValue(row, 'confidence')),
    evidence_json: (() => {
      const raw = rowValue(row, 'evidence_json')
      if (raw && typeof raw === 'object') return raw
      try { return JSON.parse(String(raw || '{}')) } catch { return {} }
    })(),
  }))
}

export const buildDirectCashActualStatementFromRows = (
  args: {
    connectionId: string
    layoutVersionId?: string | null
    periods: string[]
    rows: Array<{ period_start: string; node_id: string; amount: number }>
    endingCashAtAnchor?: number | null
  },
): (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null => {
  const periods = args.periods
  if (!periods.length) return null
  const periodIndex = new Map(periods.map((period, index) => [period, index]))
  const valuesByNode = new Map<string, Array<number | null>>()
  const addValue = (nodeId: string, period: string, amount: number) => {
    const index = periodIndex.get(period)
    if (index == null || !Number.isFinite(amount) || Math.abs(amount) <= 0.005) return
    const values = valuesByNode.get(nodeId) || emptyValues(periods.length)
    values[index] = Number(values[index] || 0) + amount
    if (Math.abs(Number(values[index] || 0)) <= 0.005) values[index] = null
    valuesByNode.set(nodeId, values)
  }
  for (const row of args.rows) {
    const period = String(row.period_start || '').slice(0, 10)
    const nodeId = String(row.node_id || '').trim()
    const amount = Number(row.amount || 0)
    if (!nodeId) continue
    addValue(nodeId, period, amount)
    if (nodeId === 'fg-forecast-cash-owner-equity') {
      addValue('fg-forecast-cash-equity', period, amount)
    } else {
      addValue(amount >= 0 ? 'fg-forecast-cash-receipts' : 'fg-forecast-cash-disbursements', period, amount)
    }
    addValue('fg-forecast-cash-net-change', period, amount)
  }
  const netValues = valuesByNode.get('fg-forecast-cash-net-change') || emptyValues(periods.length)
  const endingCashAtAnchor = Number(args.endingCashAtAnchor)
  if (Number.isFinite(endingCashAtAnchor)) {
    const beginningValues = emptyValues(periods.length)
    const endingValues = emptyValues(periods.length)
    let rollingEnding = endingCashAtAnchor
    for (let index = periods.length - 1; index >= 0; index -= 1) {
      const net = Number(netValues[index] || 0)
      endingValues[index] = rollingEnding
      const beginning = rollingEnding - net
      beginningValues[index] = Number.isFinite(beginning) ? beginning : null
      rollingEnding = beginning
    }
    valuesByNode.set('fg-forecast-cash-beginning-cash', beginningValues)
    valuesByNode.set('fg-forecast-cash-ending-cash', endingValues)
  }
  return {
    connection_id: args.connectionId,
    layout_version_id: args.layoutVersionId || '',
    statement_type: 'cash_flow',
    balance_sheet_mode: 'activity',
    periods,
    rows: directCashStatementRowsFromValues(valuesByNode, periods.length),
    warnings: ['Weekly and daily actual cash flow rows are loaded from local QuickBooks GL detail.'],
    period_kinds: periods.map(() => 'actual'),
  }
}

export const buildDirectCashActualGlDetailSql = (
  periodExpr: string,
  start: string,
  endExclusive: string,
): string => `
    WITH raw_lines AS (
      SELECT
        ${periodExpr} AS period_start,
        CAST(account_id AS VARCHAR) AS account_id,
        CAST(account_number AS VARCHAR) AS account_number,
        CAST(COALESCE(account_name, account, '') AS VARCHAR) AS account_name,
        CASE
          WHEN COALESCE(debit, 0) > 0.005 OR COALESCE(debit, 0) < -0.005 OR COALESCE(credit, 0) > 0.005 OR COALESCE(credit, 0) < -0.005 THEN -1 * (COALESCE(debit, 0) - COALESCE(credit, 0))
          ELSE COALESCE(amount, 0)
        END AS amount,
        lower(COALESCE(account_name, account, '')) AS account_label
      FROM ${QBO_GL_DETAIL_TABLE}
      WHERE TRY_CAST(txn_date AS DATE) >= DATE ${sqlString(start)}
        AND TRY_CAST(txn_date AS DATE) < DATE ${sqlString(endExclusive)}
    ),
    account_lines AS (
      SELECT period_start, account_id, account_number, account_name, amount
      FROM raw_lines
      WHERE amount IS NOT NULL
        AND amount = amount
        AND (amount > 0.005 OR amount < -0.005)
    )
    SELECT period_start, account_id, account_number, account_name, SUM(amount) AS amount
    FROM account_lines
    GROUP BY period_start, account_id, account_number, account_name
    ORDER BY period_start, account_name, account_number, account_id
  `

type DetailActualAccountRow = {
  period_start: string
  account_id?: string | null
  account_number?: string | null
  account_name?: string | null
  amount: number
}

export type DirectCashActualDetailStatements = {
  actualBasisStatement: (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null
  actualBalanceSheetStatement: (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null
}

const mappedAccountIdentity = (kind: string, value: unknown): string | null => {
  const normalized = normalizeForecastRowLabel(value)
  return normalized ? `${kind}:${normalized}` : null
}

const mappedStatementAccountKeys = (row: QuickBooksFpaStatementRow): string[] => [
  mappedAccountIdentity('id', row.account_id),
  mappedAccountIdentity('number', row.account_number),
  mappedAccountIdentity('label', row.label),
].filter((key): key is string => Boolean(key))

const hasStatementAccountIdentity = (row: QuickBooksFpaStatementRow): boolean =>
  Boolean(trimStr(row.account_id) || trimStr(row.account_number))

const detailAccountKeys = (row: DetailActualAccountRow): string[] => [
  mappedAccountIdentity('id', row.account_id),
  mappedAccountIdentity('number', row.account_number),
  mappedAccountIdentity('label', row.account_name),
].filter((key): key is string => Boolean(key))

export const buildMappedDetailActualBasisStatement = (
  sourceStatement: QuickBooksFpaStatementRender | null | undefined,
  periods: string[],
  detailRows: DetailActualAccountRow[],
): (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null => {
  if (!sourceStatement || !periods.length) return null
  const periodIndex = new Map(periods.map((period, index) => [period, index]))
  const sourceParentIds = statementParentIdsExcludingGeneratedSubtotals(sourceStatement.rows)
  const sourceRowsByKey = new Map<string, QuickBooksFpaStatementRow>()
  const mappedRows = (sourceStatement.rows || []).map((row) => cloneRowWithValues(row, emptyValues(periods.length)))

  for (const row of mappedRows) {
    if (sourceParentIds.has(row.node_id) && !hasStatementAccountIdentity(row)) continue
    if (!directCashSourceBucket(row)) continue
    for (const key of mappedStatementAccountKeys(row)) {
      if (!sourceRowsByKey.has(key)) sourceRowsByKey.set(key, row)
    }
  }

  for (const detailRow of detailRows) {
    const period = String(detailRow.period_start || '').slice(0, 10)
    const index = periodIndex.get(period)
    const amount = Number(detailRow.amount || 0)
    if (index == null || !Number.isFinite(amount) || Math.abs(amount) <= 0.005) continue
    const mappedRow = detailAccountKeys(detailRow)
      .map((key) => sourceRowsByKey.get(key))
      .find((row): row is QuickBooksFpaStatementRow => Boolean(row))
    if (!mappedRow) continue
    mappedRow.values[index] = Number(mappedRow.values[index] || 0) + amount
    if (Math.abs(Number(mappedRow.values[index] || 0)) <= 0.005) mappedRow.values[index] = null
  }

  return {
    ...sourceStatement,
    periods,
    rows: mappedRows,
    warnings: [
      ...(sourceStatement.warnings || []),
      'Daily and weekly actuals are rebuilt from GL detail using the mapped monthly statement account structure.',
    ],
    period_kinds: periods.map(() => 'actual'),
  }
}

export const buildMappedDetailActualBalanceSheetStatement = (
  sourceStatement: QuickBooksFpaStatementRender | null | undefined,
  periods: string[],
  detailRows: DetailActualAccountRow[],
): (QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null => {
  if (!sourceStatement || !periods.length) return null
  const periodIndex = new Map(periods.map((period, index) => [period, index]))
  const sourceParentIds = statementParentIdsExcludingGeneratedSubtotals(sourceStatement.rows)
  const sourceRowsByKey = new Map<string, QuickBooksFpaStatementRow>()
  const mappedRows = (sourceStatement.rows || []).map((row) => cloneRowWithValues(row, emptyValues(periods.length)))

  for (const row of mappedRows) {
    if (sourceParentIds.has(row.node_id) && !hasStatementAccountIdentity(row)) continue
    if (!balanceSheetMovementBucket(row)) continue
    for (const key of mappedStatementAccountKeys(row)) {
      if (!sourceRowsByKey.has(key)) sourceRowsByKey.set(key, row)
    }
  }

  for (const detailRow of detailRows) {
    const period = String(detailRow.period_start || '').slice(0, 10)
    const index = periodIndex.get(period)
    const amount = Number(detailRow.amount || 0)
    if (index == null || !Number.isFinite(amount) || Math.abs(amount) <= 0.005) continue
    const mappedRow = detailAccountKeys(detailRow)
      .map((key) => sourceRowsByKey.get(key))
      .find((row): row is QuickBooksFpaStatementRow => Boolean(row))
    if (!mappedRow) continue
    mappedRow.values[index] = Number(mappedRow.values[index] || 0) + amount
    if (Math.abs(Number(mappedRow.values[index] || 0)) <= 0.005) mappedRow.values[index] = null
  }

  return {
    ...sourceStatement,
    balance_sheet_mode: 'activity',
    periods,
    rows: mappedRows,
    warnings: [
      ...(sourceStatement.warnings || []),
      'Daily and weekly balance-sheet cash bridge adjustments are rebuilt from GL detail.',
    ],
    period_kinds: periods.map(() => 'actual'),
  }
}

export const loadDirectCashActualDetailStatementsFromGlDetail = async (
  appApi: ServerApiClient,
  workerClient: AppWorkerClient | null | undefined,
  connectionId: string,
  layoutVersionId: string | null | undefined,
  actualBasisStatement: QuickBooksFpaStatementRender | null | undefined,
  actualBalanceSheetStatement: QuickBooksFpaStatementRender | null | undefined,
  grain: QuickBooksFpaForecastGrain | string,
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  duckDbOpts?: Record<string, any> | null,
): Promise<DirectCashActualDetailStatements> => {
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function') {
    throw new Error('Local DuckDB is required for weekly and daily actual cash flow history.')
  }
  if (!actualBasisStatement?.rows?.length) return { actualBasisStatement: null, actualBalanceSheetStatement: null }
  const periods = actualPeriodsForForecastGrain(startDate, endDate, grain)
  if (!periods.length) return { actualBasisStatement: null, actualBalanceSheetStatement: null }
  const start = periods[0]
  const endExclusive = periodEndExclusiveIso(periods[periods.length - 1], grain)
  const cache = await appApi.prepareQuickBooksFpaCache(connectionId, { includeGl: true })
  await materializeQuickBooksGlDetailTable({
    workerClient,
    appApi,
    connectionId,
    cache,
    duckDbOpts,
  })
  const periodExpr = grain === 'daily'
    ? "strftime(CAST(txn_date AS DATE), '%Y-%m-%d')"
    : "strftime(date_trunc('week', CAST(txn_date AS DATE)), '%Y-%m-%d')"
  const result = await workerClient.duckdb_run_sql(buildDirectCashActualGlDetailSql(periodExpr, start, endExclusive), {
    ...(duckDbOpts || {}),
    previewRowLimit: 50000,
    timeoutMs: 120000,
    caller: 'QuickBooksCfoCore:forecast_actual_cash_gl_detail',
  })
  const rows = previewRowsAsObjects(result).map((row) => ({
    period_start: String(rowValue(row, 'period_start') || '').slice(0, 10),
    account_id: String(rowValue(row, 'account_id') || ''),
    account_number: String(rowValue(row, 'account_number') || ''),
    account_name: String(rowValue(row, 'account_name') || ''),
    amount: Number(rowValue(row, 'amount') || 0),
  }))
  return {
    actualBasisStatement: buildMappedDetailActualBasisStatement(
      {
        ...actualBasisStatement,
        connection_id: connectionId,
        layout_version_id: layoutVersionId || actualBasisStatement.layout_version_id || '',
      },
      periods,
      rows,
    ),
    actualBalanceSheetStatement: buildMappedDetailActualBalanceSheetStatement(
      actualBalanceSheetStatement
        ? {
          ...actualBalanceSheetStatement,
          connection_id: connectionId,
          layout_version_id: layoutVersionId || actualBalanceSheetStatement.layout_version_id || '',
        }
        : null,
      periods,
      rows,
    ),
  }
}

export const loadDirectCashActualsFromGlDetail = async (
  appApi: ServerApiClient,
  workerClient: AppWorkerClient | null | undefined,
  connectionId: string,
  layoutVersionId: string | null | undefined,
  actualBasisStatement: QuickBooksFpaStatementRender | null | undefined,
  grain: QuickBooksFpaForecastGrain | string,
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  duckDbOpts?: Record<string, any> | null,
): Promise<(QuickBooksFpaStatementRender & { period_kinds?: string[] }) | null> => {
  const statements = await loadDirectCashActualDetailStatementsFromGlDetail(
    appApi,
    workerClient,
    connectionId,
    layoutVersionId,
    actualBasisStatement,
    null,
    grain,
    startDate,
    endDate,
    duckDbOpts,
  )
  return statements.actualBasisStatement
}

export const forecastStatementFromDuckDb = async (
  workerClient: AppWorkerClient | null | undefined,
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  statementType: Exclude<StatementKind, 'cash_flow'>,
  valueCase: QuickBooksFpaForecastValueCase | string,
  selectedEndPeriod: string | null | undefined,
  futureMonths: number,
  duckDbOpts?: Record<string, any> | null,
): Promise<QuickBooksFpaMlStatementForecastResponse | null> => {
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function' || !snapshot.package) return null
  const periods = forecastPeriodsFromSnapshot(snapshot, 'month', selectedEndPeriod, futureMonths)
  if (!periods.length) return null
  const packageId = snapshot.package.id
  const scenarioKey = snapshot.scenario_key || snapshot.package.active_scenario_key || 'base'
  const valueExpr = valueCase === 'high'
    ? 'COALESCE(m.effective_high, m.effective_value)'
    : valueCase === 'low'
      ? 'COALESCE(m.effective_low, m.effective_value)'
      : 'm.effective_value'
  const periodValues = periods.map((period) => `(DATE '${period}')`).join(', ')
  const result = await workerClient.duckdb_run_sql(`
    WITH requested_periods(period_start) AS (
      VALUES ${periodValues}
    ),
    layout_rows AS (
      SELECT
        CAST(n.node_id AS VARCHAR) AS node_id,
        CAST(n.parent_node_id AS VARCHAR) AS parent_node_id,
        CAST(n.row_type AS VARCHAR) AS row_type,
        CAST(n.statement_type AS VARCHAR) AS statement_type,
        CAST(n.label AS VARCHAR) AS label,
        0 AS level,
        CAST(n.account_id AS VARCHAR) AS account_id,
        CAST(NULL AS VARCHAR) AS account_number,
        CAST(n.cash_flow_section AS VARCHAR) AS cash_flow_section,
        CAST(n.qbo_account_type AS VARCHAR) AS qbo_account_type,
        CAST(NULL AS VARCHAR) AS qbo_account_sub_type,
        CAST(n.qbo_classification AS VARCHAR) AS qbo_classification,
        CAST(n.normal_balance AS VARCHAR) AS normal_balance,
        CASE
          WHEN lower(COALESCE(CAST(n.row_type AS VARCHAR), '')) IN ('group', 'heading', 'formula')
            THEN 'true'
          ELSE 'false'
        END AS is_total,
        lower(CAST(n.is_formula AS VARCHAR)) AS is_formula,
        TRY_CAST(n.sort_order AS INTEGER) AS sort_order
      FROM fg_qbo_fpa_forecast_layout_node n
      WHERE CAST(n.package_id AS VARCHAR) = '${packageId.replace(/'/g, "''")}'
        AND CAST(n.statement_type AS VARCHAR) = '${statementType}'
    )
    SELECT
      l.*,
      strftime(p.period_start, '%Y-%m-%d') AS period_start,
      ${valueExpr} AS value
    FROM layout_rows l
    CROSS JOIN requested_periods p
    LEFT JOIN fg_qbo_fpa_forecast_line_effective m
      ON CAST(m.package_id AS VARCHAR) = '${packageId.replace(/'/g, "''")}'
      AND COALESCE(NULLIF(CAST(m.scenario_key AS VARCHAR), ''), 'base') = '${scenarioKey.replace(/'/g, "''")}'
      AND COALESCE(CAST(m.period_grain AS VARCHAR), 'month') = 'month'
      AND CAST(m.statement_type AS VARCHAR) = '${statementType}'
      AND CAST(m.node_id AS VARCHAR) = l.node_id
      AND CAST(m.period_start AS DATE) = p.period_start
    ORDER BY COALESCE(l.sort_order, 2147483647), l.node_id, p.period_start
  `, {
    ...(duckDbOpts || {}),
    previewRowLimit: Math.max(1000, periods.length * 1000),
    timeoutMs: 60000,
    caller: 'QuickBooksCfoCore:forecast_statement_artifact_query',
  })
  const periodIndex = new Map(periods.map((period, index) => [period, index]))
  const rowsByNode = new Map<string, QuickBooksFpaStatementRow & { sort_order?: number | null }>()
  const boolish = (value: unknown) => /^(true|t|1|yes)$/i.test(String(value ?? ''))
  for (const row of previewRowsAsObjects(result)) {
    const nodeId = String(rowValue(row, 'node_id') || '')
    if (!nodeId) continue
    let statementRow = rowsByNode.get(nodeId)
    if (!statementRow) {
      statementRow = {
        node_id: nodeId,
        parent_node_id: trimStr(rowValue(row, 'parent_node_id')) || null,
        row_type: String(rowValue(row, 'row_type') || 'account'),
        statement_type: statementType,
        label: String(rowValue(row, 'label') || nodeId),
        level: Number(rowValue(row, 'level') ?? 0) || 0,
        account_id: trimStr(rowValue(row, 'account_id')) || null,
        account_number: trimStr(rowValue(row, 'account_number')) || null,
        cash_flow_section: trimStr(rowValue(row, 'cash_flow_section')) || null,
        qbo_account_type: trimStr(rowValue(row, 'qbo_account_type')) || null,
        qbo_account_sub_type: trimStr(rowValue(row, 'qbo_account_sub_type')) || null,
        qbo_classification: trimStr(rowValue(row, 'qbo_classification')) || null,
        normal_balance: trimStr(rowValue(row, 'normal_balance')) || null,
        is_total: boolish(rowValue(row, 'is_total')),
        is_formula: boolish(rowValue(row, 'is_formula')),
        sort_order: Number.isFinite(Number(rowValue(row, 'sort_order'))) ? Number(rowValue(row, 'sort_order')) : null,
        values: emptyValues(periods.length),
      }
      rowsByNode.set(nodeId, statementRow)
    }
    const period = String(rowValue(row, 'period_start') || '').slice(0, 10)
    const index = periodIndex.get(period)
    const value = Number(rowValue(row, 'value'))
    if (index != null && Number.isFinite(value)) {
      statementRow.values[index] = Math.abs(value) > 0.005 ? value : null
    }
  }
  const rows = Array.from(rowsByNode.values())
    .sort((left, right) => Number(left.sort_order ?? 2147483647) - Number(right.sort_order ?? 2147483647))
    .map(({ sort_order: _sortOrder, ...row }) => row)
  const rowsById = new Map(rows.map((row) => [row.node_id, row]))
  const levelForRow = (row: QuickBooksFpaStatementRow, seen = new Set<string>()): number => {
    const parentId = trimStr(row.parent_node_id)
    if (!parentId || seen.has(row.node_id)) return 0
    const parent = rowsById.get(parentId)
    if (!parent) return 0
    seen.add(row.node_id)
    return Math.min(12, levelForRow(parent, seen) + 1)
  }
  rows.forEach((row) => {
    row.level = levelForRow(row)
  })
  if (!rows.length) return null
  return {
    warnings: [],
    contract: buildClientForecastContract(snapshot, statementType, periods),
    package: snapshot.package,
    grain: 'month',
    statement_type: statementType,
    value_case: valueCase,
    as_of_date: selectedEndPeriod || snapshot.as_of_date || null,
    statement: {
      connection_id: snapshot.package.connection_id,
      layout_version_id: snapshot.layout_nodes?.[0]?.layout_version_id || '',
      statement_type: statementType,
      balance_sheet_mode: statementType === 'balance_sheet' ? 'ending_balance' : 'activity',
      periods,
      rows,
      warnings: [],
    },
    validation: {
      ...(snapshot.validation || {}),
      status: 'ok',
      rollup_basis: 'materialized_parquet_artifacts',
      statement_rollup_basis: 'line_metadata',
      period_count: periods.length,
    },
    driver_assumptions: snapshot.driver_assumptions || {},
  }
}

export const loadDirectCashForecastFromDuckDb = async (
  workerClient: AppWorkerClient | null | undefined,
  snapshot: QuickBooksFpaMlForecastPackageSnapshotResponse,
  grain: QuickBooksFpaForecastGrain | string,
  valueCase: QuickBooksFpaForecastValueCase | string,
  selectedEndPeriod: string | null | undefined,
  futureMonths: number,
  duckDbOpts?: Record<string, any> | null,
): Promise<QuickBooksFpaMlStatementForecastResponse | null> => {
  if (!workerClient || typeof workerClient.duckdb_run_sql !== 'function' || !snapshot.package) return null
  const periods = forecastPeriodsFromSnapshot(snapshot, grain, selectedEndPeriod, futureMonths)
  if (!periods.length) return null
  const periodIndex = new Map(periods.map((period, index) => [period, index]))
  const start = periods[0]
  const last = periods[periods.length - 1]
  const packageId = snapshot.package.id
  const scenarioKey = snapshot.scenario_key || snapshot.package.active_scenario_key || 'base'
  if (grain !== 'month' && snapshotSupportsMonthlyLineMetadata(snapshot) && !snapshotHasParquetArtifacts(snapshot)) {
    const monthlyForecast = await loadDirectCashForecastFromDuckDb(
      workerClient,
      snapshot,
      'month',
      valueCase,
      selectedEndPeriod,
      futureMonths,
      duckDbOpts,
    )
    return allocateMonthlyDirectCashForecast(monthlyForecast, snapshot, grain, valueCase, selectedEndPeriod, futureMonths)
  }
  if (grain === 'month' && snapshotSupportsMonthlyLineMetadata(snapshot)) {
    // User line edits live as business-keyed deltas overlaid by the
    // `fg_qbo_fpa_forecast_line_effective` view (effective_value/low/high
    // COALESCE the overrides over the raw artifact casts). The view is warmed
    // before this query runs; a missing view is a hard failure.
    const effectiveRawAmountExpr = valueCase === 'high'
      ? 'COALESCE(m.effective_high, m.effective_value)'
      : valueCase === 'low'
        ? 'COALESCE(m.effective_low, m.effective_value)'
        : 'm.effective_value'
    const buildMonthlyRollupSql = (lineTable: string, rawAmountExpr: string) => `
      WITH forecast_lines AS (
        SELECT
          strftime(CAST(m.period_start AS DATE), '%Y-%m-01') AS period_start,
          CAST(m.node_id AS VARCHAR) AS node_id,
          lower(CAST(m.statement_type AS VARCHAR)) AS statement_type,
          ${rawAmountExpr} AS raw_amount,
          lower(
            coalesce(l.label, '') || ' ' ||
            coalesce(l.qbo_account_type, '') || ' ' ||
            coalesce(l.qbo_classification, '') || ' ' ||
            coalesce(l.cash_flow_section, '') || ' ' ||
            coalesce(l.normal_balance, '')
          ) AS haystack,
          lower(coalesce(l.qbo_classification, '')) AS classification,
          lower(coalesce(l.qbo_account_type, '')) AS account_type,
          lower(coalesce(l.row_type, '')) AS row_type,
          lower(coalesce(CAST(l.is_formula AS VARCHAR), 'false')) AS is_formula
        FROM ${lineTable} m
        JOIN fg_qbo_fpa_forecast_layout_node l
          ON CAST(l.node_id AS VARCHAR) = CAST(m.node_id AS VARCHAR)
          AND CAST(l.package_id AS VARCHAR) = CAST(m.package_id AS VARCHAR)
        WHERE CAST(m.package_id AS VARCHAR) = '${packageId.replace(/'/g, "''")}'
          AND COALESCE(CAST(m.scenario_key AS VARCHAR), 'base') = '${scenarioKey.replace(/'/g, "''")}'
          AND COALESCE(CAST(m.period_grain AS VARCHAR), 'month') = 'month'
          AND CAST(m.statement_type AS VARCHAR) IN ('profit_and_loss', 'balance_sheet')
          AND CAST(m.period_start AS DATE) >= DATE '${start}'
          AND CAST(m.period_start AS DATE) < DATE '${monthOffsetIso(last, 1)}'
      ),
      account_lines AS (
        SELECT
          period_start,
          raw_amount,
          haystack,
          classification,
          account_type,
          CASE
            WHEN haystack LIKE '%payroll%' OR haystack LIKE '%wage%' OR haystack LIKE '%salary%' OR haystack LIKE '%employee%' OR haystack LIKE '%benefit%' OR haystack LIKE '%contract labor%'
              THEN 'fg-forecast-cash-payroll'
            WHEN haystack LIKE '%tax%' OR haystack LIKE '%debt%' OR haystack LIKE '%loan%' OR haystack LIKE '%interest expense%' OR haystack LIKE '%principal%' OR haystack LIKE '%capex%' OR haystack LIKE '%capital%' OR haystack LIKE '%equipment%' OR haystack LIKE '%depreciation%' OR haystack LIKE '%amortization%'
              THEN 'fg-forecast-cash-debt-tax-capex'
            WHEN haystack LIKE '%vendor%' OR haystack LIKE '%expense%' OR haystack LIKE '%cost of goods sold%' OR haystack LIKE '%cost of sales%' OR haystack LIKE '%cogs%' OR account_type LIKE '%expense%' OR account_type LIKE '%cost%'
              THEN 'fg-forecast-cash-vendors'
            WHEN account_type LIKE '%other income%' OR haystack LIKE '%interest earned%' OR haystack LIKE '%reimbursement%' OR haystack LIKE '%reward%'
              THEN 'fg-forecast-cash-other-receipts'
            WHEN account_type LIKE '%income%' OR account_type LIKE '%revenue%' OR classification LIKE '%revenue%' OR haystack LIKE '%billing%' OR haystack LIKE '%collection%'
              THEN 'fg-forecast-cash-customer-receipts'
            ELSE 'fg-forecast-cash-other-disbursements'
          END AS node_id
        FROM forecast_lines
        WHERE raw_amount IS NOT NULL
          AND NOT isnan(raw_amount)
          AND isfinite(raw_amount)
          AND (raw_amount > 0.005 OR raw_amount < -0.005)
          AND statement_type = 'profit_and_loss'
          AND row_type = 'account'
          AND is_formula <> 'true'
      ),
      bucketed AS (
        SELECT
          period_start,
          CASE
            WHEN node_id IN ('fg-forecast-cash-payroll', 'fg-forecast-cash-vendors', 'fg-forecast-cash-debt-tax-capex', 'fg-forecast-cash-other-disbursements')
              THEN -raw_amount
            ELSE raw_amount
          END AS amount,
          node_id
        FROM account_lines
      ),
      balance_sheet_lines AS (
        SELECT
          period_start,
          node_id,
          raw_amount AS ending_amount,
          haystack,
          classification,
          account_type
        FROM forecast_lines
        WHERE raw_amount IS NOT NULL
          AND NOT isnan(raw_amount)
          AND isfinite(raw_amount)
          AND statement_type = 'balance_sheet'
          AND row_type = 'account'
          AND is_formula <> 'true'
          AND (
            classification LIKE '%equity%' OR
            account_type LIKE '%equity%' OR
            haystack LIKE '%owner%' OR
            haystack LIKE '%member%' OR
            haystack LIKE '%partner%' OR
            haystack LIKE '%draw%' OR
            haystack LIKE '%distribution%' OR
            haystack LIKE '%contribution%'
          )
          AND haystack NOT LIKE '%retained earnings%'
          AND haystack NOT LIKE '%current net income%'
          AND haystack NOT LIKE '%net income%'
          AND haystack NOT LIKE '%opening balance%'
          AND haystack NOT LIKE '%prior period%'
          AND haystack NOT LIKE '%line of credit%'
          AND haystack NOT LIKE '%loan%'
          AND haystack NOT LIKE '%note payable%'
      ),
      equity_movements AS (
        SELECT
          period_start,
          'fg-forecast-cash-owner-equity' AS node_id,
          COALESCE(
            ending_amount - LAG(ending_amount) OVER (PARTITION BY node_id ORDER BY period_start),
            LEAD(ending_amount) OVER (PARTITION BY node_id ORDER BY period_start) - ending_amount,
            0
          ) AS amount
        FROM balance_sheet_lines
      ),
      cash_movements AS (
        SELECT period_start, node_id, amount
        FROM bucketed
        UNION ALL
        SELECT period_start, node_id, amount
        FROM equity_movements
        WHERE amount IS NOT NULL
          AND amount = amount
          AND (amount > 0.005 OR amount < -0.005)
      ),
      rolled AS (
        SELECT period_start, node_id, SUM(amount) AS amount
        FROM cash_movements
        GROUP BY period_start, node_id
        UNION ALL
        SELECT
          period_start,
          CASE WHEN amount >= 0 THEN 'fg-forecast-cash-receipts' ELSE 'fg-forecast-cash-disbursements' END AS node_id,
          SUM(amount) AS amount
        FROM cash_movements
        WHERE node_id <> 'fg-forecast-cash-owner-equity'
        GROUP BY period_start, CASE WHEN amount >= 0 THEN 'fg-forecast-cash-receipts' ELSE 'fg-forecast-cash-disbursements' END
        UNION ALL
        SELECT period_start, 'fg-forecast-cash-equity' AS node_id, SUM(amount) AS amount
        FROM cash_movements
        WHERE node_id = 'fg-forecast-cash-owner-equity'
        GROUP BY period_start
        UNION ALL
        SELECT period_start, 'fg-forecast-cash-net-change' AS node_id, SUM(amount) AS amount
        FROM cash_movements
        GROUP BY period_start
      )
      SELECT period_start, node_id, amount
      FROM rolled
      ORDER BY period_start, node_id
    `
    // The `_effective` view (delta overlay) is part of the data contract: it
    // is warmed before this query runs, and a failure here is a hard failure
    // surfaced through the page's error state — never a silent render of the
    // base artifact with user edits missing.
    const result: any = await workerClient.duckdb_run_sql(
      buildMonthlyRollupSql('fg_qbo_fpa_forecast_line_effective', effectiveRawAmountExpr),
      {
        ...(duckDbOpts || {}),
        previewRowLimit: 5000,
        timeoutMs: 60000,
        caller: 'QuickBooksCfoCore:forecast_monthly_line_effective_rollup_query',
      },
    )
    const valuesByNode = new Map<string, Array<number | null>>()
    for (const row of previewRowsAsObjects(result)) {
      const period = String(rowValue(row, 'period_start') || '').slice(0, 10)
      const nodeId = String(rowValue(row, 'node_id') || '')
      const amount = Number(rowValue(row, 'amount') || 0)
      const index = periodIndex.get(period)
      if (index == null || !nodeId || !Number.isFinite(amount) || Math.abs(amount) <= 0.005) continue
      const values = valuesByNode.get(nodeId) || emptyValues(periods.length)
      values[index] = Number(values[index] || 0) + amount
      valuesByNode.set(nodeId, values)
    }
    const includedNetCashChange = (valuesByNode.get('fg-forecast-cash-net-change') || []).reduce<number>((sum, value) => sum + Number(value || 0), 0)
    return {
      warnings: [],
      contract: buildClientForecastContract(snapshot, 'cash_flow', periods),
      package: snapshot.package,
      grain,
      statement_type: 'cash_flow',
      value_case: valueCase,
      as_of_date: selectedEndPeriod || snapshot.as_of_date || null,
      statement: {
        connection_id: snapshot.package.connection_id,
        layout_version_id: snapshot.layout_nodes?.[0]?.layout_version_id || '',
        statement_type: 'cash_flow',
        balance_sheet_mode: 'activity',
        periods,
        rows: directCashStatementRowsFromValues(valuesByNode, periods.length),
        warnings: [],
      },
      validation: {
        ...(snapshot.validation || {}),
        status: 'ok',
        rollup_basis: snapshot.validation?.rollup_basis || 'materialized_parquet_artifacts',
        monthly_rollup_basis: 'line_metadata',
        period_count: periods.length,
        included_net_cash_change: includedNetCashChange,
      },
      driver_assumptions: snapshot.driver_assumptions || {},
    }
  }
  const endExclusive = grain === 'month'
    ? monthOffsetIso(last, 1)
    : grain === 'weekly'
      ? addDaysIso(last, 7)
      : addDaysIso(last, 1)
  const periodExpr = grain === 'daily'
    ? "strftime(CAST(forecast_date AS DATE), '%Y-%m-%d')"
    : grain === 'weekly'
      ? "strftime(date_trunc('week', CAST(forecast_date AS DATE)), '%Y-%m-%d')"
      : "strftime(CAST(forecast_date AS DATE), '%Y-%m-01')"
  const amountExpr = valueCase === 'high'
    ? 'COALESCE(high_amount, expected_amount)'
    : valueCase === 'low'
      ? 'COALESCE(low_amount, expected_amount)'
      : 'expected_amount'
  const result = await workerClient.duckdb_run_sql(`
    WITH filtered AS (
      SELECT
        ${periodExpr} AS period_start,
        TRY_CAST(regexp_replace(CAST(${amountExpr} AS VARCHAR), '[^0-9.\\-]', '', 'g') AS DOUBLE) AS amount,
        lower(
          coalesce(cash_event_type, '') || ' ' ||
          coalesce(source_type, '') || ' ' ||
          coalesce(source_id, '') || ' ' ||
          coalesce(counterparty_name, '') || ' ' ||
          coalesce(CAST(evidence_json AS VARCHAR), '')
        ) AS haystack,
        lower(coalesce(cash_event_type, '') || ' ' || coalesce(source_type, '')) AS summary_key
    FROM fg_qbo_fpa_forecast_cash_daily
      WHERE CAST(forecast_date AS DATE) >= DATE '${start}'
        AND CAST(forecast_date AS DATE) < DATE '${endExclusive}'
        AND CAST(package_id AS VARCHAR) = '${packageId.replace(/'/g, "''")}'
        AND COALESCE(CAST(scenario_key AS VARCHAR), 'base') = '${scenarioKey.replace(/'/g, "''")}'
    ),
    base AS (
      SELECT period_start, amount, haystack
      FROM filtered
      WHERE amount IS NOT NULL
        AND NOT (
          summary_key LIKE '%net_operating_cash%' OR
          summary_key LIKE '%summary%'
        )
    ),
    bucketed AS (
      SELECT
        period_start,
        amount,
        CASE
          WHEN haystack LIKE '%owner%' OR haystack LIKE '%member%' OR haystack LIKE '%partner%' OR haystack LIKE '%draw%' OR haystack LIKE '%distribution%' OR haystack LIKE '%contribution%' OR haystack LIKE '%equity%'
            THEN 'fg-forecast-cash-owner-equity'
          WHEN amount >= 0 AND (haystack LIKE '%customer%' OR haystack LIKE '%receipt%' OR haystack LIKE '%ar%')
            THEN 'fg-forecast-cash-customer-receipts'
          WHEN amount >= 0
            THEN 'fg-forecast-cash-other-receipts'
          WHEN haystack LIKE '%payroll%' OR haystack LIKE '%wage%' OR haystack LIKE '%salary%'
            THEN 'fg-forecast-cash-payroll'
          WHEN haystack LIKE '%tax%' OR haystack LIKE '%debt%' OR haystack LIKE '%loan%' OR haystack LIKE '%capex%' OR haystack LIKE '%capital%'
            THEN 'fg-forecast-cash-debt-tax-capex'
          WHEN haystack LIKE '%vendor%' OR haystack LIKE '%disbursement%' OR haystack LIKE '%expense%'
            THEN 'fg-forecast-cash-vendors'
          ELSE 'fg-forecast-cash-other-disbursements'
        END AS node_id
      FROM base
    ),
    rolled AS (
      SELECT period_start, node_id, SUM(amount) AS amount
      FROM bucketed
      GROUP BY period_start, node_id
      UNION ALL
      SELECT
        period_start,
        CASE WHEN amount >= 0 THEN 'fg-forecast-cash-receipts' ELSE 'fg-forecast-cash-disbursements' END AS node_id,
        SUM(amount) AS amount
      FROM bucketed
      WHERE node_id <> 'fg-forecast-cash-owner-equity'
      GROUP BY period_start, CASE WHEN amount >= 0 THEN 'fg-forecast-cash-receipts' ELSE 'fg-forecast-cash-disbursements' END
      UNION ALL
      SELECT period_start, 'fg-forecast-cash-equity' AS node_id, SUM(amount) AS amount
      FROM bucketed
      WHERE node_id = 'fg-forecast-cash-owner-equity'
      GROUP BY period_start
      UNION ALL
      SELECT period_start, 'fg-forecast-cash-net-change' AS node_id, SUM(amount) AS amount
      FROM bucketed
      GROUP BY period_start
    )
    SELECT period_start, node_id, amount
    FROM rolled
    ORDER BY period_start, node_id
  `, {
    ...(duckDbOpts || {}),
    previewRowLimit: 5000,
    timeoutMs: 60000,
    caller: 'QuickBooksCfoCore:forecast_cash_rollup_query',
  })
  const valuesByNode = new Map<string, Array<number | null>>()
  for (const row of previewRowsAsObjects(result)) {
    const period = String(rowValue(row, 'period_start') || '').slice(0, 10)
    const nodeId = String(rowValue(row, 'node_id') || '')
    const amount = Number(rowValue(row, 'amount') || 0)
    const index = periodIndex.get(period)
    if (index == null || !nodeId || !Number.isFinite(amount) || Math.abs(amount) <= 0.005) continue
    const values = valuesByNode.get(nodeId) || emptyValues(periods.length)
    values[index] = Number(values[index] || 0) + amount
    valuesByNode.set(nodeId, values)
  }
  const includedNetCashChange = (valuesByNode.get('fg-forecast-cash-net-change') || []).reduce<number>((sum, value) => sum + Number(value || 0), 0)
  return {
    warnings: [],
    contract: buildClientForecastContract(snapshot, 'cash_flow', periods),
    package: snapshot.package,
    grain,
    statement_type: 'cash_flow',
    value_case: valueCase,
    as_of_date: selectedEndPeriod || snapshot.as_of_date || null,
    statement: {
      connection_id: snapshot.package.connection_id,
      layout_version_id: snapshot.layout_nodes?.[0]?.layout_version_id || '',
      statement_type: 'cash_flow',
      balance_sheet_mode: 'activity',
      periods,
      rows: directCashStatementRowsFromValues(valuesByNode, periods.length),
      warnings: [],
    },
    validation: {
      ...(snapshot.validation || {}),
      status: 'ok',
      rollup_basis: snapshot.validation?.rollup_basis || 'materialized_parquet_artifacts',
      period_count: periods.length,
      included_net_cash_change: includedNetCashChange,
    },
    driver_assumptions: snapshot.driver_assumptions || {},
  }
}
